import { Fragment } from "react";
import { Pill } from "@/components/account/Shell";

/**
 * The spreadsheet kit — the same build as /account/products, for every list in the back office.
 * One fixed column set per sheet, a header row that explains each column in one line, one record
 * per row, and an optional full-width "expand" row under the record you clicked.
 */
export const gridOf = (cols) => ({ gridTemplateColumns: cols.map((c) => c.w).join(" ") });
export const pinned = (key, bg) => (key === "actions" ? `sticky right-0 z-[1] ${bg} shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.25)]` : "");
export const chip = "inline-flex items-center gap-1 whitespace-nowrap border px-1.5 py-px text-[11px] font-semibold uppercase";

export const STATUS_CLS = {
    pending: "border-[#b54708] bg-[#fffaeb] text-[#b54708]",
    pending_payment: "border-[#b54708] bg-[#fffaeb] text-[#b54708]",
    pending_review: "border-[#b54708] bg-[#fffaeb] text-[#b54708]",
    queued: "border-[#b54708] bg-[#fffaeb] text-[#b54708]",
    booked: "border-[#1a56db] bg-[#eff4ff] text-[#1a56db]",
    in_production: "border-[#1a56db] bg-[#eff4ff] text-[#1a56db]",
    scheduled: "border-[#1a56db] bg-[#eff4ff] text-[#1a56db]",
    active: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    paid: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    ready: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    sent: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    done: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    open: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    completed: "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]",
    closed: "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]",
    sold: "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]",
    rejected: "border-[#b42318] bg-[#fef3f2] text-[#b42318]",
    cancelled: "border-[#b42318] bg-[#fef3f2] text-[#b42318]",
    failed: "border-[#b42318] bg-[#fef3f2] text-[#b42318]",
    suspended: "border-[#b42318] bg-[#fef3f2] text-[#b42318]",
    no_show: "border-[#b54708] bg-[#fffaeb] text-[#b54708]",
    removed: "border-[#d0d0d0] bg-[#f5f5f5] text-[#4a4a4a]",
};

export const Tag = ({ status, children, testid, cls }) => (
    <span data-testid={testid} className={`${chip} ${cls || STATUS_CLS[status] || STATUS_CLS.completed}`}>
        {children ?? String(status || "").replace(/_/g, " ")}
    </span>
);

export const Cell = ({ children, className = "", title, testid }) => (
    <div className={`flex min-w-0 items-center gap-1 px-1.5 ${className}`} title={title} data-testid={testid}>{children}</div>
);

export const Dash = ({ why = "Not applicable" }) => (
    <span className="w-full text-center text-[#9a9a9a]" title={why} aria-label={why}>—</span>
);

/** Plain text in a cell — truncates, and the full text sits in the tooltip. */
export const Txt = ({ children, bold, sub, mono, right, testid, cls = "", title }) => (
    <span data-testid={testid} title={title ?? (typeof children === "string" ? children : undefined)}
        className={`min-w-0 truncate text-[12px] ${bold ? "font-semibold text-[#1a1a1a]" : sub ? "text-[#4a4a4a]" : "font-medium text-[#1a1a1a]"} ${mono ? "tabular-nums" : ""} ${right ? "w-full text-right" : ""} ${cls}`}>
        {children}
    </span>
);

/** Two lines in one cell: the thing, then one quieter line under it. */
export const Two = ({ a, b, testid, title }) => (
    <span className="min-w-0" data-testid={testid} title={title}>
        <span className="block truncate text-[12px] font-semibold text-[#1a1a1a]">{a}</span>
        {b ? <span className="block truncate text-[11px] text-[#4a4a4a]">{b}</span> : null}
    </span>
);

export const IconBtn = ({ onClick, disabled, testid, title, danger, children, href }) => {
    const cls = `flex h-6 w-6 shrink-0 items-center justify-center border border-[#d0d0d0] bg-white disabled:opacity-40 ${danger
        ? "text-[#b42318] hover:border-[#b42318]" : "text-[#1a1a1a] hover:border-[#1a1a1a]"}`;
    if (href) return <a href={href} target="_blank" rel="noreferrer" data-testid={testid} title={title} aria-label={title} className={cls}>{children}</a>;
    return (
        <button onClick={onClick} disabled={disabled} data-testid={testid} title={title} aria-label={title} className={cls}>
            {children}
        </button>
    );
};

/** One row of pills: a mono label, then the choices with counts. */
export function PillRow({ label, choices, value, onPick, testid }) {
    return (
        <div className="flex flex-wrap items-center gap-1" data-testid={testid}>
            {label && <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4a4a4a]">{label}</span>}
            {choices.map((c) => (
                <Pill key={c.key} active={value === c.key} count={c.count} testid={`${testid}-${c.key || "all"}`} onClick={() => onPick(c.key)}>{c.label}</Pill>
            ))}
        </div>
    );
}

/** A strip of filter rows, ruled top and bottom, that sits between the page head and the sheet. */
export function Filters({ children, testid }) {
    return <div className="mt-2 space-y-1.5 border-y border-[#e5e5e5] py-2" data-testid={testid}>{children}</div>;
}

/** KPI tiles in one ruled strip — numbers first, one quiet line under each. */
export function Tiles({ items, testid }) {
    return (
        <div className="grid gap-px border border-[#d0d0d0] bg-[#d0d0d0] sm:grid-cols-2 lg:grid-cols-4" data-testid={testid}>
            {items.map(([label, value, note, tid]) => (
                <div key={label} className="bg-white p-2.5" data-testid={tid}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">{label}</p>
                    <p className="mt-1 text-xl font-bold tabular-nums tracking-tight text-[#1a1a1a]">{value}</p>
                    {note && <p className="mt-0.5 text-[11px] text-[#4a4a4a]">{note}</p>}
                </div>
            ))}
        </div>
    );
}

export function SheetHeader({ cols, testid = "sheet" }) {
    return (
        <div className="sticky top-0 z-10 grid items-end border-b-2 border-[#1a1a1a] bg-[#fafafa]" style={gridOf(cols)} data-testid={`${testid}-header`}>
            {cols.map((c) => (
                <div key={c.key} className={`px-1.5 py-1.5 ${pinned(c.key, "bg-[#fafafa]")}`} title={c.sub ? `${c.label} — ${c.sub}` : c.label} data-testid={`${testid}-col-${c.key}`}>
                    <p className="text-[11px] font-bold uppercase leading-tight text-[#1a1a1a]">{c.label}</p>
                    {c.sub && <p className="text-[10px] leading-tight text-[#4a4a4a]">{c.sub}</p>}
                </div>
            ))}
        </div>
    );
}

export function SheetRow({ cols, cells, testid, cls = "", bg = "bg-white", onClick, active }) {
    const Comp = onClick ? "button" : "div";
    return (
        <Comp type={onClick ? "button" : undefined} onClick={onClick} data-testid={testid}
            className={`grid min-h-[40px] w-full items-center border-t border-[#d0d0d0] text-left ${bg} ${onClick ? "hover:bg-[#fafafa]" : ""} ${active ? "bg-[#eff4ff]" : ""} ${cls}`}
            style={gridOf(cols)}>
            {cols.map((c) => (
                <div key={c.key} className={`flex min-w-0 items-center self-stretch py-1 ${pinned(c.key, active ? "bg-[#eff4ff]" : bg)}`}>
                    {cells[c.key] ?? <Cell><Dash /></Cell>}
                </div>
            ))}
        </Comp>
    );
}

/**
 * `cells(row)` returns one node per column key; `expand(row)` (optional) renders a full-width block
 * under that row — the open order, the thread, the form. `active(row)` paints the open row.
 */
export function Sheet({ cols, rows, rowKey, cells, rowCls, rowBg, rowTestid, onRowClick, active, expand, empty = "Nothing here yet.", testid = "sheet", minWidth = 960 }) {
    return (
        <div className="overflow-x-auto">
            <div style={{ minWidth }} data-testid={testid}>
                <SheetHeader cols={cols} testid={testid} />
                {rows.map((r, i) => {
                    const on = active ? active(r) : false;
                    return (
                        <Fragment key={rowKey(r, i) ?? i}>
                            <SheetRow cols={cols} cells={cells(r)} testid={rowTestid?.(r)} cls={rowCls?.(r) || ""}
                                bg={rowBg?.(r) || "bg-white"} onClick={onRowClick ? () => onRowClick(r) : undefined} active={on} />
                            {on && expand && (
                                <div className="border-t border-[#d0d0d0] border-l-4 border-l-[#1a56db] bg-[#fafafa] p-2.5" data-testid={`${testid}-expand`}>
                                    {expand(r)}
                                </div>
                            )}
                        </Fragment>
                    );
                })}
                {!rows.length && (
                    <p className="border-t border-[#d0d0d0] px-3 py-6 text-center text-sm text-[#4a4a4a]" data-testid={`${testid}-empty`}>{empty}</p>
                )}
            </div>
        </div>
    );
}

/** The "come back when you have a space" screen every locked back-office page shows. */
export function Locked({ icon: Icon, title, body, cta = "See the spaces", to = "/account/sell", testid }) {
    return (
        <div className="mx-auto max-w-2xl py-16 text-center" data-testid={testid}>
            {Icon && <Icon size={30} className="mx-auto text-[#4a4a4a]" />}
            <h1 className="mt-3 text-xl font-bold tracking-tight text-[#1a1a1a]">{title}</h1>
            {body && <p className="mt-2 text-sm text-[#4a4a4a]">{body}</p>}
            <a href={to} className="mt-4 inline-flex items-center gap-1.5 border border-[#1a56db] bg-[#1a56db] px-2 py-1 text-[13px] font-semibold text-white bo-inv hover:bg-[#1544b0]"
                data-testid={`${testid}-cta`}>{cta}</a>
        </div>
    );
}
